import {
  ITransaction,
  Maybe,
  MerchantDetails,
  PayOverTimeStatus,
  PayOverTimeTransactionState,
  Tag,
  VxlText,
} from '@varo/graphql-schema-types';
import { useEffect, useRef } from 'react';
import { segmentClient } from 'services/analytics/segment/segment';

export const PAY_OVER_TIME_TAG_TEXT = 'Pay Over Time';

interface PayOverTimeAnalyticsTransaction extends Pick<
  ITransaction,
  'id' | 'type'
> {
  value: Pick<ITransaction['value'], 'amount'>;
  merchant?: Pick<MerchantDetails, 'name'>;
  payOverTime?: Maybe<Pick<PayOverTimeTransactionState, 'status'>>;
}

interface PayOverTimeAnalyticsRow {
  onClick?: Maybe<{
    transaction: PayOverTimeAnalyticsTransaction;
  }>;
  primaryText: Pick<VxlText, 'copy'>;
  tags: Array<Pick<Tag, 'text'>>;
}

export interface PayOverTimeAnalyticsGroup {
  children: PayOverTimeAnalyticsRow[];
  transactions?: PayOverTimeAnalyticsRow[];
}

const usePayOverTimeTransactionTaggedAnalytics = (
  transactionGroups?: PayOverTimeAnalyticsGroup[],
) => {
  const loggedTransactionIds = useRef(new Set());

  useEffect(() => {
    transactionGroups?.forEach((group) => {
      const transactionRows = group.transactions ?? group.children;

      transactionRows.forEach((row) => {
        const transaction = row.onClick?.transaction;
        const hasPayOverTimeTag = row.tags.some(
          ({ text }) => text === PAY_OVER_TIME_TAG_TEXT,
        );
        const isEligible =
          hasPayOverTimeTag ||
          transaction?.payOverTime?.status === PayOverTimeStatus.Eligible;

        if (
          !isEligible ||
          !transaction ||
          loggedTransactionIds.current.has(transaction.id)
        ) {
          return;
        }

        const amount = Number(transaction.value.amount);
        const merchantName = transaction.merchant?.name ?? row.primaryText.copy;

        loggedTransactionIds.current.add(transaction.id);
        void segmentClient.logEvent('Pay Over Time Transaction Eligible', {
          category: 'pay_over_time',
          transaction_id: transaction.id,
          transaction_type: transaction.type.toLowerCase(),
          merchant_name: merchantName,
          amount,
        });
      });
    });
  }, [transactionGroups]);
};

export default usePayOverTimeTransactionTaggedAnalytics;
